// Theme — light/dark toggle.
// Storage key: design-theme. Source of truth = <html data-theme> (written by the inline script in
// index.html before React to prevent a white flash); this is just its reactive wrapper.
// The toggle shows the target-state icon (dark shows sun / light shows moon).

import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'design-theme';

export function getTheme(): Theme {
  const current = document.documentElement.dataset.theme;
  if (current === 'light' || current === 'dark') return current;
  // Dark is the primary visual; default to dark when no preference (light stays switchable)
  return 'dark';
}

const listeners = new Set<() => void>();
function emit() {
  listeners.forEach(l => l());
}

export function setTheme(t: Theme): void {
  try { localStorage.setItem(STORAGE_KEY, t); } catch { /* fine in privacy mode */ }
  document.documentElement.dataset.theme = t;
  emit();
}

export function toggleTheme(): void {
  setTheme(getTheme() === 'dark' ? 'light' : 'dark');
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme);
}
